import { useCallback, useEffect, useRef, useState } from 'react'
import { Icon } from '@iconify/react'
import type { MiniGameProps } from './MascotMiniGame'
import type { GameEvent } from './mascot-game-types'
import { GameConfirm } from './GameConfirm'
import { timeLabel } from './mascot-game-view'
import { FrisbeeScene } from './FrisbeeScene'
import {
  REDUCED_VICTORY_DURATION,
  VICTORY_DURATION,
  victoryPose,
} from './frisbee-victory'
import {
  advanceRun,
  jump,
  newRun,
  RUN_DURATION,
  runCoins,
  runScore,
  runSpeed,
  START_SPEED,
  MAX_PICKUP_COINS,
} from './frisbee-engine'
import styles from './FrisbeeGame.module.css'

export function FrisbeeGame({
  session,
  mascot,
  avatar,
  onFinish,
  onBack,
  onRestart,
  restartBlockedReason = '',
  restartLabel = 'Tentar novamente',
  autoStart = false,
  playSound,
  resultMessage,
  error,
  busy,
  wallet,
}: MiniGameProps) {
  const simulation = useRef(newRun())
  const events = useRef<GameEvent[]>([])
  const [run, setRun] = useState(newRun)
  const [phase, setPhase] = useState<
    'ready' | 'playing' | 'paused' | 'victory' | 'result'
  >(autoStart ? 'playing' : 'ready')
  const [saved, setSaved] = useState(false)
  const [confirmLeave, setConfirmLeave] = useState(false)
  const saving = useRef(false)
  const controls = useRef<HTMLButtonElement>(null)
  const results = useRef<HTMLDivElement>(null)
  const scene = useRef<HTMLDivElement>(null)
  const victoryClock = useRef(0)
  const [victoryAt, setVictoryAt] = useState(0)
  const [reducedVictory] = useState(
    () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
  )
  const callbacks = useRef({ onFinish, playSound })
  useEffect(() => {
    callbacks.current = { onFinish, playSound }
  }, [onFinish, playSound])
  const save = useCallback(async () => {
    if (saving.current) return
    saving.current = true
    try {
      setSaved(await callbacks.current.onFinish(events.current))
    } finally {
      saving.current = false
    }
  }, [])
  const doJump = useCallback(() => {
    if (jump(simulation.current, events.current)) callbacks.current.playSound(1)
  }, [])
  useEffect(() => {
    if (phase === 'victory') {
      scene.current?.focus({ preventScroll: true })
      scene.current?.scrollIntoView?.({ block: 'start', behavior: 'instant' })
    }
    if (phase !== 'result') return
    results.current?.focus({ preventScroll: true })
    results.current?.scrollIntoView?.({ block: 'start', behavior: 'instant' })
  }, [phase])
  useEffect(() => {
    if (phase !== 'victory' || confirmLeave) return
    let frame = 0
    let previous = performance.now()
    const duration = reducedVictory ? REDUCED_VICTORY_DURATION : VICTORY_DURATION
    const tick = (now: number) => {
      const delta = now - previous
      previous = now
      // Preserve the celebration when a phone or tab is briefly suspended.
      if (!document.hidden && delta <= 250) victoryClock.current += delta
      setVictoryAt(Math.min(duration, victoryClock.current))
      if (victoryClock.current >= duration) setPhase('result')
      else frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [phase, confirmLeave, reducedVictory])
  useEffect(() => {
    if (phase !== 'playing') return
    let frame = 0
    let previous = performance.now()
    let target = simulation.current.at
    const tick = (now: number) => {
      const delta = now - previous
      previous = now
      // Never skip collisions or consume play time while a tab/device is suspended.
      if (delta > 250) {
        setPhase('paused')
        return
      }
      target += delta
      const before = simulation.current.cleared
      const coinsBefore = simulation.current.collected
      advanceRun(simulation.current, target, session)
      setRun({ ...simulation.current })
      if (simulation.current.collected > coinsBefore) callbacks.current.playSound(3)
      else if (simulation.current.cleared > before) callbacks.current.playSound(2)
      if (simulation.current.ended) {
        events.current.push({ at: simulation.current.at, value: 1 })
        setPhase(simulation.current.at === RUN_DURATION ? 'victory' : 'result')
        callbacks.current.playSound(simulation.current.at === RUN_DURATION ? 3 : 0)
        void save()
      } else frame = requestAnimationFrame(tick)
    }
    const pause = () => setPhase('paused')
    const hidden = () => {
      if (document.hidden) pause()
    }
    const modalOpened = (event: FocusEvent) => {
      if (
        event.target instanceof HTMLElement &&
        event.target.closest('[role="alertdialog"]')
      )
        pause()
    }
    const key = (event: KeyboardEvent) => {
      if (event.code === 'KeyP' || event.code === 'Escape') {
        event.preventDefault()
        event.stopPropagation()
        pause()
        return
      }
      const jumpKey =
        ['Space', 'ArrowUp', 'KeyW'].includes(event.code) ||
        [' ', 'Spacebar', 'ArrowUp', 'w', 'W'].includes(event.key)
      if (!jumpKey) return
      if (
        event.target instanceof HTMLElement &&
        event.target.closest(
          'input, textarea, select, [contenteditable]:not([contenteditable="false"])'
        )
      )
        return
      // Space belongs to the running game, even after another button gets focus.
      // Prevent native scrolling/activation on held keys too, without auto-jumping.
      event.preventDefault()
      if (event.repeat) return
      doJump()
    }
    frame = requestAnimationFrame(tick)
    window.addEventListener('keydown', key, true)
    window.addEventListener('blur', pause)
    document.addEventListener('visibilitychange', hidden)
    document.addEventListener('focusin', modalOpened)
    controls.current?.focus({ preventScroll: true })
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('keydown', key, true)
      window.removeEventListener('blur', pause)
      document.removeEventListener('visibilitychange', hidden)
      document.removeEventListener('focusin', modalOpened)
    }
  }, [phase, session, doJump, save])
  const score = runScore(run)
  const won = run.at === RUN_DURATION
  const celebration = victoryPose(victoryAt, reducedVictory)
  const stage = [
    mascot.species === 'jaguar' && mascot.backdrop !== 'farm'
      ? 'Aventura na selva'
      : mascot.backdrop === 'farm'
        ? 'Aventura na fazendinha'
        : 'Passeio no parque',
    'Vento nas orelhas',
    'Patas a todo vapor',
    'A última arrancada',
  ][Math.min(3, Math.floor(run.at / 150000))]
  const leave = () => {
    if (phase === 'ready' || saved) onBack()
    else {
      if (phase === 'playing') setPhase('paused')
      setConfirmLeave(true)
    }
  }
  return (
    <section className={styles.game} data-phase={phase} aria-label="Busca do frisbee">
      <header className={styles.header}>
        <button type="button" className={styles.quiet} onClick={leave} disabled={busy}>
          <Icon icon="mdi:arrow-left" /> Jogos
        </button>
        <span className={styles.wordmark}>
          PARQUE DAS PATINHAS <b>Busca do frisbee</b>
        </span>
        <div className={styles.headerRight}>
          {wallet}
          {phase === 'playing' && (
            <button
              type="button"
              className={styles.quiet}
              onClick={() => setPhase('paused')}
              aria-label="Pausar corrida"
            >
              <Icon icon="mdi:pause" />
            </button>
          )}
        </div>
      </header>
      <div className={styles.dashboard}>
        <div>
          <small>PONTUAÇÃO</small>
          <strong>{score.toString().padStart(5, '0')}</strong>
        </div>
        <div>
          <small>TEMPO CORRENDO</small>
          <strong>
            {timeLabel(Math.floor(run.at / 1000))}
            <em> / 10:00</em>
          </strong>
        </div>
        <div>
          <small>RITMO</small>
          <strong>
            {(runSpeed(run.at) / START_SPEED).toFixed(1)}
            <em> ×</em>
          </strong>
        </div>
        <div className={styles.coinStat}>
          <small>MOEDAS PREVISTAS</small>
          <strong>
            <Icon icon="mdi:circle-multiple" /> {runCoins(score) + run.collected}
            <em> / {18 + Math.min(MAX_PICKUP_COINS, session.coins?.length ?? 0)}</em>
          </strong>
          <span
            className={styles.coinBreakdown}
            aria-label="Moedas coletadas no percurso"
          >
            {run.collected}/{Math.min(MAX_PICKUP_COINS, session.coins?.length ?? 0)}{' '}
            coletadas · {runCoins(score)} por pontos
          </span>
        </div>
      </div>
      <div
        ref={scene}
        className={styles.stage}
        tabIndex={-1}
        onContextMenu={(event) => event.preventDefault()}
        onDragStart={(event) => event.preventDefault()}
      >
        <FrisbeeScene
          run={run}
          session={session}
          mascot={mascot}
          avatar={avatar}
          moving={phase === 'playing'}
          victoryAt={
            won ? (phase === 'victory' ? victoryAt : VICTORY_DURATION) : undefined
          }
          reducedVictory={reducedVictory}
        />
        <span className={styles.chapter}>
          {phase === 'ready' ? 'UM FRISBEE. MIL AVENTURAS.' : stage}
        </span>
        {phase === 'playing' && (
          <button
            ref={controls}
            type="button"
            className={styles.touchSurface}
            aria-label="Pular obstáculo"
            onPointerDown={(event) => {
              if (event.button !== 0) return
              event.preventDefault()
              doJump()
            }}
            onClick={(event) => {
              if (event.detail === 0) doJump()
            }}
          >
            <span className={styles.touchHint} aria-hidden="true">
              <span className={styles.touchPrompt}>
                <Icon icon="mdi:gesture-tap" /> Toque aqui para pular
              </span>
              <span>Deixe o caminho livre para os olhos.</span>
            </span>
          </button>
        )}
        {phase === 'ready' && (
          <div className={styles.intro}>
            <span className={styles.kicker}>A PRÓXIMA BRINCADEIRA FAVORITA</span>
            <h2>
              Corre que é seu<span>!</span>
            </h2>
            <p>
              O frisbee foi longe. {mascot.name || 'Seu companheiro'} tem uma missão:
              correr, saltar e buscar de volta. Pule para pegar as moedas douradas!
            </p>
            <button
              type="button"
              className={styles.primary}
              onClick={() => {
                callbacks.current.playSound(3)
                setPhase('playing')
              }}
            >
              Lançar e correr <Icon icon="mdi:arrow-top-right" />
            </button>
            <small>Começa tranquilo. Acelera sem parar. Chegue aos 10 minutos!</small>
          </div>
        )}
        {phase === 'paused' && !confirmLeave && (
          <div className={styles.overlay}>
            <span className={styles.kicker}>UMA PAUSA PARA RESPIRAR</span>
            <h2>
              {mascot.species === 'jaguar' && mascot.backdrop !== 'farm'
                ? 'A selva espera.'
                : mascot.backdrop === 'farm'
                  ? 'A fazendinha espera.'
                  : 'O parque espera.'}
            </h2>
            <p>Seu tempo fica parado. Pronto para continuar?</p>
            <button
              type="button"
              className={styles.primary}
              onClick={() => setPhase('playing')}
              autoFocus
            >
              Continuar corrida <Icon icon="mdi:play" />
            </button>
          </div>
        )}
        {phase === 'victory' && celebration.gone && (
          <div className={styles.victoryBanner}>
            <div className={styles.confetti} aria-hidden="true">
              {Array.from({ length: 18 }, (_, index) => (
                <i
                  key={index}
                  style={{
                    left: `${5 + index * 5.2}%`,
                    animationDelay: `${(index % 5) * -0.18}s`,
                    background: ['#e4aa40', '#6c9972', '#e88e70'][index % 3],
                  }}
                />
              ))}
            </div>
            <span className={styles.kicker}>DEZ MINUTOS. UMA GRANDE CONQUISTA.</span>
            <h2>Parabéns, dupla!</h2>
            <p>{mascot.name || 'Seu companheiro'} voltou com o frisbee!</p>
          </div>
        )}
      </div>
      <div className={styles.journey}>
        <span>
          <Icon icon="mdi:paw" /> Largada
        </span>
        <div
          role="progressbar"
          aria-label="Caminho até o frisbee"
          aria-valuemin={0}
          aria-valuemax={600}
          aria-valuenow={Math.floor(run.at / 1000)}
        >
          <i style={{ width: `${(run.at / RUN_DURATION) * 100}%` }} />
        </div>
        <span>🥏 10 min</span>
      </div>
      {phase === 'result' ? (
        <div
          ref={results}
          className={styles.results}
          role="region"
          aria-label="Resultado da corrida"
          tabIndex={-1}
        >
          <div>
            <span className={styles.kicker}>
              {won ? 'MISSÃO CUMPRIDA · FRISBEE RECUPERADO' : 'FOI UMA BELA CORRIDA'}
            </span>
            <h2>
              {won
                ? 'Parabéns! O frisbee é de vocês.'
                : 'Um tropeço. E vontade de ir além.'}
            </h2>
            <p>
              {run.cleared} obstáculos superados · {score} pontos ·{' '}
              {timeLabel(Math.floor(run.at / 1000))} de aventura
            </p>
            <p>
              {run.collected} moedas coletadas + {runCoins(score)} por pontos ={' '}
              {run.collected + runCoins(score)} moedas previstas
            </p>
          </div>
          <p role="status">
            {busy
              ? saved
                ? 'Preparando a próxima corrida…'
                : 'Guardando suas recompensas…'
              : saved
                ? resultMessage || 'Sua corrida foi salva.'
                : 'Salve sua corrida para guardar os pontos e as moedas.'}
          </p>
          {error && (
            <p role="alert" className={styles.error}>
              {error}
            </p>
          )}
          <div className={styles.resultActions}>
            {onRestart && (
              <button
                type="button"
                className={styles.primary}
                onClick={onRestart}
                disabled={!saved || busy || !!restartBlockedReason}
              >
                <Icon icon="mdi:restart" /> {restartLabel}
              </button>
            )}
            {!saved && (
              <button
                type="button"
                className={styles.primary}
                onClick={() => void save()}
                disabled={busy}
              >
                Tentar salvar resultado
              </button>
            )}
            <button
              type="button"
              className={saved && !onRestart ? styles.primary : styles.quiet}
              onClick={leave}
              disabled={busy}
            >
              Voltar aos jogos <Icon icon="mdi:arrow-right" />
            </button>
          </div>
          {saved && restartBlockedReason && !busy && <p>{restartBlockedReason}</p>}
        </div>
      ) : phase === 'victory' ? (
        <div className={styles.victoryFooter}>
          <p role="status">
            {celebration.gone
              ? 'Parabéns! Vocês completaram a aventura!'
              : celebration.caught
                ? 'Pegou! Hora de levar o troféu para casa.'
                : 'O último salto… vai pegar!'}
          </p>
          <button
            type="button"
            className={styles.quiet}
            onClick={() => setPhase('result')}
          >
            Ver resultado <Icon icon="mdi:arrow-right" />
          </button>
        </div>
      ) : (
        <footer className={styles.instructions}>
          <div>
            <Icon icon="mdi:gesture-tap" />
            <p>
              <strong>Um toque. Um salto.</strong>
              <span>
                Toque na parte de baixo do cenário. No PC, espaço / ↑ para pular. P para
                pausar.
              </span>
            </p>
          </div>
          <div>
            <Icon icon="mdi:leaf" />
            <p>
              <strong>Cada trecho vale a pena.</strong>
              <span>10 pontos/s + 25 por obstáculo. Uma colisão encerra a corrida.</span>
            </p>
          </div>
          <p className={styles.fineprint}>
            Pegue até 30 moedas nos saltos e ganhe mais 1 a cada 500 pontos, até 18 pela
            pontuação. O total está sujeito à redução e ao limite diário da carteira. A
            energia já foi usada ao preparar a partida. Pausas longas podem expirar a
            sessão após 30 minutos.
          </p>
        </footer>
      )}
      {confirmLeave && (
        <GameConfirm
          title="Sair desta corrida?"
          description="A corrida sem resultado salvo não entrega recompensas."
          leaveLabel="Sair da corrida"
          onContinue={() => setConfirmLeave(false)}
          onLeave={onBack}
        />
      )}
    </section>
  )
}
